import React from "react";
import { useIntl } from "react-intl";
import { dataLanguage } from "../../../i18n/translateRequest";
import "./israelcard.css";

// Bundle card for the Israeli operators, drawn from the product's own
// fields: allowances, validity, flags, title, description and price. There
// is no artwork to load, so it renders the same for every product and in
// every theme. The operator's colour comes from its name.
//
// The endpoints behind it do not agree on names - the peletalk/EPRS products
// are serialised straight from their SQL columns (amount, provider), Azy's
// are hand-built (price, sub_provider) - so every field is read through a
// list of aliases. A product with nothing in the viewer's language (Azy
// carries Hebrew only, with "N/A" in the Arabic columns) borrows another
// language rather than rendering with no name at all.

const ACCENTS = {
  cellcom: "#7b2cbf",
  "cellcom 2": "#7b2cbf",
  pelephone: "#0057b8",
  partner: "#ff7a00",
  "golan telecom": "#00a3e0",
  "012 mobile": "#e5007d",
  mobile019: "#d62828",
  azy: "#d62828",
  hot: "#e0004d",
};

// First value present among several possible keys, ignoring case.
const pick = (obj, ...keys) => {
  if (!obj) return null;
  for (const key of keys) {
    const found = Object.keys(obj).find((k) => k.toLowerCase() === key.toLowerCase());
    if (found && obj[found] !== undefined && obj[found] !== null) return obj[found];
  }
  return null;
};

const text = (v) => {
  if (v === undefined || v === null) return "";
  const s = String(v).trim();
  return !s || s.toLowerCase() === "n/a" ? "" : s;
};

const isOn = (v) => ["true", "1", "yes"].includes(text(v).toLowerCase());

// First of several values that carries anything worth showing.
const firstText = (...values) => {
  for (const v of values) {
    const s = text(v);
    if (s) return s;
  }
  return "";
};

// "4000" -> "4,000"; anything non-numeric is shown as it is.
const num = (v) => {
  const s = text(v);
  if (!s || s === "0") return "";
  const n = Number(s);
  return Number.isFinite(n) ? n.toLocaleString() : s;
};

// "5 MONTH" / "30 DAY" / "1 YEAR" -> localised "5 months" / "30 days".
// Azy stores the number on its own, and a bare "30" on a calendar icon says
// nothing at all, so a missing unit is read as days.
const useValidity = (raw) => {
  const intl = useIntl();
  const s = text(raw);
  if (!s || s === "0") return "";
  const m = s.match(/^(\d+)\s*([a-z]*)/i);
  if (!m) return s;
  const n = Number(m[1]);
  const unit = (m[2] || "day").toLowerCase().replace(/s$/, "");
  const key = { day: n === 1 ? "day" : "days", week: n === 1 ? "week" : "weeks",
                month: n === 1 ? "month" : "months", year: n === 1 ? "year" : "years" }[unit];
  return key ? `${n} ${intl.formatMessage({ id: key })}` : s;
};

const Icon = ({ name }) => {
  switch (name) {
    case "data":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 9a15 15 0 0 1 20 0" /><path d="M5.5 12.5a10 10 0 0 1 13 0" /><path d="M9 16a5 5 0 0 1 6 0" /><circle cx="12" cy="19.5" r="1" fill="currentColor" />
        </svg>
      );
    case "minutes":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2z" />
        </svg>
      );
    case "sms":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.5-4.5A8 8 0 1 1 21 12z" />
        </svg>
      );
    case "calendar":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 11h18" />
        </svg>
      );
    default:
      return null;
  }
};

const IsraelCard = ({ data, logo, smallLogo, cardStyle = {}, isDarkTheme = false, isStatic = false, isSelected = false }) => {
  const intl = useIntl();
  const isRTL = intl.locale === "ar" || intl.locale === "he";

  const provider = text(pick(data, "provider", "Provider", "sub_provider", "SubProvider")).toLowerCase();
  const accent = ACCENTS[provider] || ACCENTS[provider.split(" ")[0]] || "#fa5818";

  const price = parseFloat(pick(data, "amount", "Amount", "price", "Price") || 0);
  const priceText = Number.isFinite(price) && price > 0 ? price.toLocaleString() : "";
  const gb = num(pick(data, "gb", "GB"));
  const minutes = num(pick(data, "min", "Min"));
  const sms = num(pick(data, "sms", "SMS"));
  const palMinutes = num(pick(data, "pal_min"));
  const validity = useValidity(pick(data, "valid", "Valid"));
  const is5G = isOn(pick(data, "5g_enabled", "5G_enabled"));
  const fixedIp = isOn(pick(data, "fixed_ip"));
  const needsBasic = isOn(pick(data, "must_add_basic_card"));
  const renewable = isOn(pick(data, "renew", "autorenew", "AutoRenew"));

  const title = firstText(
    dataLanguage(data, "ar_title", "en_title", "he_title"),
    pick(data, "en_title"), pick(data, "he_title"), pick(data, "ar_title"),
    pick(data, "title", "product")
  );
  const description = firstText(
    dataLanguage(data, "ar_des", "en_des", "he_des"),
    pick(data, "en_des"), pick(data, "he_des"), pick(data, "ar_des")
  );

  const t = (id) => intl.formatMessage({ id });

  // Several products carry the word rather than a number ("unlimited"), and
  // it needs the viewer's language like any other label.
  const allowance = (v) => (v && v.toLowerCase() === "unlimited" ? t("unlimited") : v);

  // The biggest thing the bundle gives is the hero; the rest go in the row.
  const all = [
    gb && { key: "data", value: allowance(gb), unit: "GB", label: t("Data") },
    minutes && { key: "minutes", value: allowance(minutes), label: t("Minutes") },
    sms && { key: "sms", value: allowance(sms), label: t("SMS") },
    palMinutes && { key: "pal", value: allowance(palMinutes), label: t("Palestinian minutes"), icon: "minutes" },
  ].filter(Boolean);
  const [hero, ...rest] = all;

  // Nothing but a price to show means this is plain credit, not a bundle:
  // the price becomes the hero instead of a lone unexplained number.
  const isCredit = all.length === 0;

  const classes = [
    "il-card",
    isDarkTheme ? "il-card--dark" : "",
    isStatic ? "il-card--static" : "",
    isCredit ? "il-card--credit" : "",
    isSelected ? "il-card--selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={classes} dir={isRTL ? "rtl" : "ltr"} style={{ "--il-accent": accent, ...cardStyle }}>
      <div className="il-card__head">
        {(logo || smallLogo) && (
          <div className="il-card__logo">
            <img src={smallLogo || logo} alt="" />
          </div>
        )}
        <div className="il-card__badges">
          {is5G && <span className="il-card__badge">5G</span>}
          {renewable && <span className="il-card__badge">{t("Renewable")}</span>}
          {fixedIp && <span className="il-card__badge il-card__badge--soft">{t("Fixed IP")}</span>}
        </div>
        {validity && (
          <span className="il-card__valid">
            <Icon name="calendar" />
            {validity}
          </span>
        )}
        {isSelected && (
          <span className="il-card__check" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12.5l5 5L20 6.5" />
            </svg>
          </span>
        )}
      </div>

      {hero ? (
        <div className="il-card__hero">
          <span className="il-card__hero-value">{hero.value}</span>
          {hero.unit && <span className="il-card__hero-unit">{hero.unit}</span>}
          <span className="il-card__hero-label">{hero.label}</span>
        </div>
      ) : (
        <div className="il-card__hero il-card__hero--credit">
          <span className="il-card__hero-value">{priceText || "—"}</span>
          <span className="il-card__hero-unit">₪</span>
          <span className="il-card__hero-label">{t("Credit")}</span>
        </div>
      )}

      {/* Always rendered, empty included: every card has to reserve the same
          room for the allowance row or the grid goes ragged. */}
      <div className="il-card__stats">
        {rest.map((s) => (
          <div className="il-card__stat" key={s.key} title={s.label}>
            <Icon name={s.icon || s.key} />
            <b>{s.value}</b>
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      <div className="il-card__body">
        {title && <h3 className="il-card__title">{title}</h3>}
        {description && <p className="il-card__desc">{description}</p>}
      </div>

      {/* Credit already shows its price as the hero, so the foot would only
          repeat it - but it stays in place, empty, to keep every card the
          same height. */}
      <div className="il-card__foot">
        {!isCredit && priceText ? (
          <span className="il-card__price">
            {priceText}
            <span className="il-card__cur">₪</span>
          </span>
        ) : (
          <span />
        )}
        {needsBasic && <span className="il-card__note">{t("Requires basic card")}</span>}
      </div>
    </div>
  );
};

export default IsraelCard;
